import type { RuntimeConfig } from '@/config/env'
import { runtimeConfig } from '@/config/env'
import { createSupabaseClient } from '@/infrastructure/supabase/client'
import type { ClientRepository } from '@/features/clients/application/ClientRepository'
import { createFixtureClientRepository } from '@/features/clients/infrastructure/fixtures/FixtureClientRepository'
import { createResilientClientRepository } from '@/features/clients/infrastructure/ResilientClientRepository'
import { createSupabaseClientRepository } from '@/features/clients/infrastructure/supabase/SupabaseClientRepository'
import type { ContractRepository } from '@/features/contracts/application/ContractRepository'
import { createFixtureContractRepository } from '@/features/contracts/infrastructure/fixtures/FixtureContractRepository'
import { createResilientContractRepository } from '@/features/contracts/infrastructure/ResilientContractRepository'
import { createSupabaseContractRepository } from '@/features/contracts/infrastructure/supabase/SupabaseContractRepository'
import type { ContractSettingsRepository } from '@/features/contracts/application/ContractSettingsRepository'
import type { ProposalDeliveryRepository } from '@/features/contracts/application/ProposalDeliveryRepository'
import { createFixtureProposalDeliveryRepository } from '@/features/contracts/infrastructure/fixtures/FixtureProposalDeliveryRepository'
import { createSupabaseProposalDeliveryRepository } from '@/features/contracts/infrastructure/supabase/SupabaseProposalDeliveryRepository'
import { createFixtureContractSettingsRepository } from '@/features/contracts/infrastructure/fixtures/FixtureContractSettingsRepository'
import { createResilientContractSettingsRepository } from '@/features/contracts/infrastructure/ResilientContractSettingsRepository'
import { createSupabaseContractSettingsRepository } from '@/features/contracts/infrastructure/supabase/SupabaseContractSettingsRepository'
import type { DashboardRepository } from '@/features/dashboard/application/DashboardRepository'
import { createFixtureDashboardRepository } from '@/features/dashboard/infrastructure/fixtures/FixtureDashboardRepository'
import { createSupabaseDashboardRepository } from '@/features/dashboard/infrastructure/supabase/SupabaseDashboardRepository'
import type { DocumentRepository } from '@/features/documents/application/DocumentRepository'
import { createFixtureDocumentRepository } from '@/features/documents/infrastructure/fixtures/FixtureDocumentRepository'
import { createResilientDocumentRepository } from '@/features/documents/infrastructure/ResilientDocumentRepository'
import { createSupabaseDocumentRepository } from '@/features/documents/infrastructure/supabase/SupabaseDocumentRepository'
import type { LeadRepository } from '@/features/leads/application/LeadRepository'
import { createFixtureLeadRepository } from '@/features/leads/infrastructure/fixtures/FixtureLeadRepository'
import { createResilientLeadRepository } from '@/features/leads/infrastructure/ResilientLeadRepository'
import { createSupabaseLeadRepository } from '@/features/leads/infrastructure/supabase/SupabaseLeadRepository'
import type { MarkerRepository } from '@/features/markers/application/MarkerRepository'
import { createFixtureMarkerRepository } from '@/features/markers/infrastructure/fixtures/FixtureMarkerRepository'
import { createResilientMarkerRepository } from '@/features/markers/infrastructure/ResilientMarkerRepository'
import { createSupabaseMarkerRepository } from '@/features/markers/infrastructure/supabase/SupabaseMarkerRepository'
import type { FieldMissionRepository } from '@/features/missions/application/FieldMissionRepository'
import { createFixtureFieldMissionRepository } from '@/features/missions/infrastructure/fixtures/FixtureFieldMissionRepository'
import { createSupabaseFieldMissionRepository } from '@/features/missions/infrastructure/supabase/SupabaseFieldMissionRepository'
import type { OperationsCenterRepository } from '@/features/operationsCenter/application/OperationsCenterRepository'
import { createFixtureOperationsCenterRepository } from '@/features/operationsCenter/infrastructure/fixtures/FixtureOperationsCenterRepository'
import { createSupabaseOperationsCenterRepository } from '@/features/operationsCenter/infrastructure/supabase/SupabaseOperationsCenterRepository'
import type { RouteRepository } from '@/features/routes/application/RouteRepository'
import type { RouteToolsRepository } from '@/features/routes/application/RouteToolsRepository'
import type { RoutePathRepository } from '@/features/routes/application/RoutePathRepository'
import { createFixtureRoutePaths } from '@/features/routes/infrastructure/fixtures/fixtureRoutePaths'
import { createSupabaseRoutePaths } from '@/features/routes/infrastructure/supabase/supabaseRoutePaths'
import { createFixtureRouteTools } from '@/features/routes/infrastructure/fixtures/fixtureRouteTools'
import { createSupabaseRouteTools } from '@/features/routes/infrastructure/supabase/supabaseRouteTools'
import { createFixtureRouteRepository } from '@/features/routes/infrastructure/fixtures/FixtureRouteRepository'
import { createResilientRouteRepository } from '@/features/routes/infrastructure/ResilientRouteRepository'
import { createSupabaseRouteRepository } from '@/features/routes/infrastructure/supabase/SupabaseRouteRepository'
import type { QuoteRepository } from '@/features/quotes/application/QuoteRepository'
import { createFixtureQuoteRepository } from '@/features/quotes/infrastructure/fixtures/FixtureQuoteRepository'
import { createResilientQuoteRepository } from '@/features/quotes/infrastructure/ResilientQuoteRepository'
import { createSupabaseQuoteRepository } from '@/features/quotes/infrastructure/supabase/SupabaseQuoteRepository'
import type { InvoiceRepository } from '@/features/finance/application/InvoiceRepository'
import { createFixtureInvoiceRepository } from '@/features/finance/infrastructure/fixtures/FixtureInvoiceRepository'
import { createResilientInvoiceRepository } from '@/features/finance/infrastructure/ResilientInvoiceRepository'
import { createSupabaseInvoiceRepository } from '@/features/finance/infrastructure/supabase/SupabaseInvoiceRepository'
import type { TerritoryRepository } from '@/features/territory/application/TerritoryRepository'
import { createFixtureTerritoryRepository } from '@/features/territory/infrastructure/fixtures/FixtureTerritoryRepository'
import { createResilientTerritoryRepository } from '@/features/territory/infrastructure/ResilientTerritoryRepository'
import { createSupabaseTerritoryRepository } from '@/features/territory/infrastructure/supabase/SupabaseTerritoryRepository'
import type { QuoteTaxRepository } from '@/features/settings/application/QuoteTaxRepository'
import { createFixtureQuoteTaxRepository } from '@/features/settings/infrastructure/fixtures/FixtureQuoteTaxRepository'
import { createResilientQuoteTaxRepository } from '@/features/settings/infrastructure/ResilientQuoteTaxRepository'
import { createSupabaseQuoteTaxRepository } from '@/features/settings/infrastructure/supabase/SupabaseQuoteTaxRepository'
import type { ModuleVisibilityRepository } from '@/features/settings/application/ModuleVisibilityRepository'
import { createFixtureModuleVisibilityRepository } from '@/features/settings/infrastructure/fixtures/FixtureModuleVisibilityRepository'
import { createResilientModuleVisibilityRepository } from '@/features/settings/infrastructure/ResilientModuleVisibilityRepository'
import { createSupabaseModuleVisibilityRepository } from '@/features/settings/infrastructure/supabase/SupabaseModuleVisibilityRepository'
import type { EmailRepository } from '@/features/email/application/EmailRepository'
import { createFixtureEmailRepository } from '@/features/email/infrastructure/FixtureEmailRepository'
import { createSupabaseEmailRepository } from '@/features/email/infrastructure/SupabaseEmailRepository'
import type { SubmissionRepository } from '@/features/submissions/application/SubmissionRepository'
import { createFixtureSubmissionRepository } from '@/features/submissions/infrastructure/fixtures/FixtureSubmissionRepository'
import { createResilientSubmissionRepository } from '@/features/submissions/infrastructure/ResilientSubmissionRepository'
import { createSupabaseSubmissionRepository } from '@/features/submissions/infrastructure/supabase/SupabaseSubmissionRepository'
import type { OverviewAdminKpisRepository } from '@/features/overview/application/OverviewAdminKpisRepository'
import type { OverviewMapRepository } from '@/features/overview/application/OverviewMapRepository'
import type { OverviewSitesRepository } from '@/features/overview/application/OverviewSitesRepository'
import type { OverviewContractValueRepository } from '@/features/overview/application/OverviewContractValueRepository'
import { createFixtureOverviewAdminKpisRepository } from '@/features/overview/infrastructure/fixtures/FixtureOverviewAdminKpisRepository'
import { createFixtureOverviewMapRepository } from '@/features/overview/infrastructure/fixtures/FixtureOverviewMapRepository'
import { createFixtureOverviewSitesRepository } from '@/features/overview/infrastructure/fixtures/FixtureOverviewSitesRepository'
import { createFixtureOverviewContractValueRepository } from '@/features/overview/infrastructure/fixtures/FixtureOverviewContractValueRepository'
import { createResilientOverviewMapRepository } from '@/features/overview/infrastructure/ResilientOverviewMapRepository'
import { createSupabaseOverviewMapRepository } from '@/features/overview/infrastructure/supabase/SupabaseOverviewMapRepository'
import { createSupabaseOverviewAdminKpisRepository } from '@/features/overview/infrastructure/supabase/SupabaseOverviewAdminKpisRepository'
import { createSupabaseOverviewSitesRepository } from '@/features/overview/infrastructure/supabase/SupabaseOverviewSitesRepository'
import { createSupabaseOverviewContractValueRepository } from '@/features/overview/infrastructure/supabase/SupabaseOverviewContractValueRepository'

import type { EmployeeRepository } from '@/features/employees/application/EmployeeRepository'
import type { ReportRepository } from '@/features/reports/application/ReportRepository'
import { createFixtureReportRepository } from '@/features/reports/infrastructure/fixtures/FixtureReportRepository'
import { createSupabaseReportRepository } from '@/features/reports/infrastructure/supabase/SupabaseReportRepository'
import { createFixtureEmployeeRepository } from '@/features/employees/infrastructure/fixtures/FixtureEmployeeRepository'
import { createSupabaseEmployeeRepository } from '@/features/employees/infrastructure/supabase/SupabaseEmployeeRepository'
import type { SmsRepository } from '@/features/sms/application/SmsRepository'
import { createFixtureSmsRepository } from '@/features/sms/infrastructure/FixtureSmsRepository'
import { createSupabaseSmsRepository } from '@/features/sms/infrastructure/SupabaseSmsRepository'
import type { VnextContractRepository } from '@/features/contracts-vnext/application/VnextContractRepository'
import { createFixtureVnextContractRepository } from '@/features/contracts-vnext/infrastructure/FixtureVnextContractRepository'
import { createSupabaseVnextContractRepository } from '@/features/contracts-vnext/infrastructure/SupabaseVnextContractRepository'
import type { NotificationRepository } from '@/features/notifications/application/NotificationRepository'
import { createFixtureNotificationRepository } from '@/features/notifications/infrastructure/FixtureNotificationRepository'
import { createSupabaseNotificationRepository } from '@/features/notifications/infrastructure/SupabaseNotificationRepository'
import type { MarketingRepository } from '@/features/google/application/MarketingRepository'
import { createFixtureMarketingRepository } from '@/features/google/infrastructure/FixtureMarketingRepository'
import { createSupabaseMarketingRepository } from '@/features/google/infrastructure/SupabaseMarketingRepository'
import type { SectorRepository } from '@/features/sectors/application/SectorRepository'
import type { DemandesV3Repository } from '@/features/demandes-v3/application/DemandesV3Repository'
import { createFixtureDemandesV3Repository } from '@/features/demandes-v3/infrastructure/FixtureDemandesV3Repository'
import { createSupabaseDemandesV3Repository } from '@/features/demandes-v3/infrastructure/SupabaseDemandesV3Repository'
import { createFixtureSectorRepository } from '@/features/sectors/infrastructure/FixtureSectorRepository'
import { createSupabaseSectorRepository } from '@/features/sectors/infrastructure/SupabaseSectorRepository'
import type { DispatchRepository } from '@/features/dispatch/application/DispatchRepository'
import { createFixtureDispatchRepository } from '@/features/dispatch/infrastructure/FixtureDispatchRepository'
import { createSupabaseDispatchRepository } from '@/features/dispatch/infrastructure/SupabaseDispatchRepository'

type SupabaseClient = ReturnType<typeof createSupabaseClient>

export interface AppDependencies {
  config: RuntimeConfig
  supabase: SupabaseClient | null
  clients: ClientRepository
  contracts: ContractRepository
  contractSettings: ContractSettingsRepository
  contractsVnext: VnextContractRepository
  leads: LeadRepository
  markers: MarkerRepository
  missions: FieldMissionRepository
  operationsCenter: OperationsCenterRepository
  routes: RouteRepository
  routeTools: RouteToolsRepository
  routePaths: RoutePathRepository
  quotes: QuoteRepository
  quoteTaxes: QuoteTaxRepository
  moduleVisibility: ModuleVisibilityRepository
  documents: DocumentRepository
  territory: TerritoryRepository
  invoices: InvoiceRepository
  dashboard: DashboardRepository
  email: EmailRepository
  submissions: SubmissionRepository
  overviewMap: OverviewMapRepository
  overviewAdminKpis: OverviewAdminKpisRepository
  overviewSites: OverviewSitesRepository
  overviewContractValue: OverviewContractValueRepository
  employees: EmployeeRepository
  reports: ReportRepository
  sms: SmsRepository
  proposalDelivery: ProposalDeliveryRepository
  notifications: NotificationRepository
  marketing: MarketingRepository
  sectors: SectorRepository
  demandesV3: DemandesV3Repository
  dispatch: DispatchRepository
}

export function composeDependencies(config: RuntimeConfig): AppDependencies {
  // Le type de la configuration ne se laisse pas rétrécir plus loin dans la
  // fonction : l'URL et la clé sont retenues ici, au seul endroit où elles
  // existent avec certitude.
  const remote =
    config.dataSource === 'supabase' ? { url: config.supabaseUrl, anonKey: config.supabaseAnonKey } : null
  const supabase = remote ? createSupabaseClient(remote) : null
  const fixtureClients = createFixtureClientRepository()
  const fixtureRoutes = createFixtureRouteRepository()
  const fixtureQuotes = createFixtureQuoteRepository()
  const fixtureQuoteTaxes = createFixtureQuoteTaxRepository()
  const fixtureModuleVisibility = createFixtureModuleVisibilityRepository()
  const fixtureDocuments = createFixtureDocumentRepository()
  const fixtureLeads = createFixtureLeadRepository()
  const fixtureMarkers = createFixtureMarkerRepository()
  const fixtureContracts = createFixtureContractRepository()
  const fixtureContractSettings = createFixtureContractSettingsRepository()
  const fixtureTerritory = createFixtureTerritoryRepository()
  const fixtureInvoices = createFixtureInvoiceRepository()
  const fixtureEmail = createFixtureEmailRepository()
  const fixtureSubmissions = createFixtureSubmissionRepository()
  return {
    config,
    supabase,
    clients:
      supabase && config.enableLegacyClientReads
        ? createResilientClientRepository(
            createSupabaseClientRepository(supabase),
            createFixtureClientRepository('fixture-fallback'),
          )
        : fixtureClients,
    contracts: supabase
      ? createResilientContractRepository(
          createSupabaseContractRepository(supabase, remote?.url ?? '', remote?.anonKey ?? ''),
          createFixtureContractRepository('fixture-fallback'),
        )
      : fixtureContracts,
    contractSettings: supabase
      ? createResilientContractSettingsRepository(
          createSupabaseContractSettingsRepository(supabase),
          createFixtureContractSettingsRepository('fixture-fallback'),
        )
      : fixtureContractSettings,
    // Contrats vNext : aucun repli, un faux contrat de 30 adresses se lirait comme un fait.
    contractsVnext: supabase
      ? createSupabaseVnextContractRepository(supabase)
      : createFixtureVnextContractRepository(),
    leads: supabase
      ? createResilientLeadRepository(
          createSupabaseLeadRepository(supabase),
          createFixtureLeadRepository('fixture-fallback'),
        )
      : fixtureLeads,
    markers: supabase
      ? createResilientMarkerRepository(
          createSupabaseMarkerRepository(supabase),
          createFixtureMarkerRepository(),
        )
      : fixtureMarkers,
    // Aucune bascule vers les fixtures : des missions de démonstration ne remplacent jamais les vraies.
    missions: supabase
      ? createSupabaseFieldMissionRepository(supabase)
      : createFixtureFieldMissionRepository(),
    // Centre des opérations (ADR-019) : démonstration aux données fictives sans base, jamais en repli d'une vraie.
    operationsCenter: supabase
      ? createSupabaseOperationsCenterRepository(supabase)
      : createFixtureOperationsCenterRepository(),
    routes: supabase
      ? createResilientRouteRepository(
          createSupabaseRouteRepository(supabase),
          createFixtureRouteRepository('fixture-fallback'),
        )
      : fixtureRoutes,
    // Outils Routes : jamais de repli sur la démonstration (un résultat inventé passerait pour un vrai calcul).
    routeTools: supabase ? createSupabaseRouteTools(supabase) : createFixtureRouteTools(),
    routePaths: supabase ? createSupabaseRoutePaths(supabase) : createFixtureRoutePaths(fixtureRoutes),
    quotes: supabase
      ? createResilientQuoteRepository(
          createSupabaseQuoteRepository(supabase),
          createFixtureQuoteRepository('fixture-fallback'),
        )
      : fixtureQuotes,
    quoteTaxes: supabase
      ? createResilientQuoteTaxRepository(
          createSupabaseQuoteTaxRepository(supabase),
          createFixtureQuoteTaxRepository('fixture-fallback'),
        )
      : fixtureQuoteTaxes,
    moduleVisibility: supabase
      ? createResilientModuleVisibilityRepository(
          createSupabaseModuleVisibilityRepository(supabase),
          createFixtureModuleVisibilityRepository('fixture-fallback'),
        )
      : fixtureModuleVisibility,
    documents: supabase
      ? createResilientDocumentRepository(
          createSupabaseDocumentRepository(supabase),
          createFixtureDocumentRepository('fixture-fallback'),
        )
      : fixtureDocuments,
    // Le tableau de bord n'a pas de repli : un indicateur d'exploitation
    // fictif se lit comme un fait, et fausserait une décision.
    dashboard: supabase ? createSupabaseDashboardRepository(supabase) : createFixtureDashboardRepository(),
    territory: supabase
      ? createResilientTerritoryRepository(
          createSupabaseTerritoryRepository(supabase),
          createFixtureTerritoryRepository('fixture-fallback'),
        )
      : fixtureTerritory,
    invoices: supabase
      ? createResilientInvoiceRepository(
          createSupabaseInvoiceRepository(supabase, remote?.url ?? '', remote?.anonKey ?? ''),
          createFixtureInvoiceRepository('fixture-fallback'),
        )
      : fixtureInvoices,
    // Une boîte réelle ne doit jamais se rabattre silencieusement sur des messages fictifs.
    email: supabase
      ? createSupabaseEmailRepository(
          supabase as unknown as Parameters<typeof createSupabaseEmailRepository>[0],
        )
      : fixtureEmail,
    submissions: supabase
      ? createResilientSubmissionRepository(
          createSupabaseSubmissionRepository(supabase),
          createFixtureSubmissionRepository('fixture-fallback'),
        )
      : fixtureSubmissions,
    overviewMap: supabase
      ? createResilientOverviewMapRepository(
          createSupabaseOverviewMapRepository(supabase),
          createFixtureOverviewMapRepository('fixture-fallback'),
        )
      : createFixtureOverviewMapRepository(),
    // Aucun repli : un faux montant se lirait comme un fait (mandat Vue d'ensemble, jamais de 0 $ masquant une erreur).
    overviewAdminKpis: supabase
      ? createSupabaseOverviewAdminKpisRepository(supabase)
      : createFixtureOverviewAdminKpisRepository(),
    // Vue d'ensemble V3 : aucun repli fixture, une erreur reste affichée comme telle.
    overviewSites: supabase
      ? createSupabaseOverviewSitesRepository(supabase)
      : createFixtureOverviewSitesRepository(),
    overviewContractValue: supabase
      ? createSupabaseOverviewContractValueRepository(supabase)
      : createFixtureOverviewContractValueRepository(),
    // Aucun repli : un faux employé ou un faux compte se lirait comme un fait et fausserait une décision d'accès.
    employees: supabase ? createSupabaseEmployeeRepository(supabase) : createFixtureEmployeeRepository(),
    // Aucun repli : un faux compte ou un faux fichier se lirait comme une vraie donnée de l'entreprise.
    reports: supabase ? createSupabaseReportRepository(supabase) : createFixtureReportRepository(),
    // Aucun repli : un faux état d'activation SMS se lirait comme un fait.
    sms: supabase ? createSupabaseSmsRepository(supabase) : createFixtureSmsRepository(),
    // Aucun repli : un faux état d'envoi ou de vérification de numéro se lirait comme un fait.
    proposalDelivery: supabase
      ? createSupabaseProposalDeliveryRepository(supabase)
      : createFixtureProposalDeliveryRepository(),
    // Aucun repli : un faux compteur ou une fausse notification se liraient comme des faits.
    notifications: supabase
      ? createSupabaseNotificationRepository(supabase)
      : createFixtureNotificationRepository(),
    // Module Google : aucun repli, une fausse dépense ou une fausse conversion se liraient comme des faits.
    marketing: supabase ? createSupabaseMarketingRepository(supabase) : createFixtureMarketingRepository(),
    // Module Secteurs : aucun repli, un faux secteur ou une fausse disponibilité se liraient comme des faits.
    sectors: supabase ? createSupabaseSectorRepository(supabase) : createFixtureSectorRepository(),
    // Demandes-V3 : aucun repli, un faux classement se lirait comme le vrai.
    demandesV3: supabase ? createSupabaseDemandesV3Repository(supabase) : createFixtureDemandesV3Repository(),
    // Routes › Répartition : aucun repli, un faux plan se lirait comme la répartition réelle.
    dispatch: supabase ? createSupabaseDispatchRepository(supabase) : createFixtureDispatchRepository(),
  }
}

export const appDependencies = composeDependencies(runtimeConfig)
